/**
 * Quiz analytics engine.
 *
 * Owns one quiz session's worth of analytics: it generates the session id,
 * builds the runtime-knowable `base` from the config, detects the device,
 * stamps each emitted event, batches them, and hands flushed batches to an
 * injected transport.
 *
 * Design constraints (ADR-39 runtime client + CLAUDE.md):
 *   - Batch: queue in memory, flush ~400ms after the first un-flushed event.
 *   - Flush immediately on `quiz_completed`, and on visibilitychange(hidden)
 *     / pagehide so the terminal batch isn't lost on navigation.
 *   - Bounded retry on transport failure, then drop. NEVER throw into the UI.
 *   - Drop events once the in-memory queue exceeds a safe cap.
 *   - Hard-disabled in preview mode (?preview=true): no session, no queue,
 *     no flush — `enqueue()` is a no-op.
 *
 * The transport is the ONLY swap point for the Phase-2 delivery mechanism;
 * nothing in this file changes when the real adapter lands.
 */

import { detectDevice } from '@/analytics/page-context';
import { BeaconTransport } from '@/analytics/transports/beacon';
import type {
  AnalyticsBatch,
  AnalyticsEmit,
  AnalyticsTransport,
  QuizAnalyticsBase,
  QuizAnalyticsEvent,
} from '@/analytics/types';
import type { RuntimeQuizConfig } from '@/types/runtime';

// Events emitted within this window of the first un-flushed event ship in
// one batch. Confirmed contract value.
const FLUSH_INTERVAL_MS = 400;
// Safety valve: if the transport stays down and the queue grows past this,
// stop accumulating rather than leak memory.
const MAX_QUEUE = 100;
// Best-effort delivery: try a few times, then drop the batch.
const MAX_SEND_ATTEMPTS = 3;

export interface AnalyticsEngine {
  enqueue(event: AnalyticsEmit): void;
  /** Flush + detach listeners + cancel the pending timer. Idempotent. */
  dispose(): void;
}

const NOOP_ENGINE: AnalyticsEngine = {
  enqueue: () => {},
  dispose: () => {},
};

function isPreviewMode(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('preview') === 'true';
  } catch {
    return false;
  }
}

function uuid(): string {
  try {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch {
    // Non-secure context or missing API — fall through.
  }
  // Fallback id. Not cryptographically strong; only needs to be unique
  // enough for client-side ingestion dedupe.
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * Build the analytics engine for one quiz mount. Returns an inert no-op
 * engine in preview mode so Studio preview never sessions, queues, or flushes.
 *
 * @param transport defaults to the real BeaconTransport (direct POST to the
 *   stream). Inject a different transport (e.g. StubTransport for console
 *   debugging, or a fake in tests) to override.
 */
export function createAnalyticsEngine(
  config: RuntimeQuizConfig,
  transport?: AnalyticsTransport,
): AnalyticsEngine {
  if (isPreviewMode()) return NOOP_ENGINE;
  return new RealAnalyticsEngine(config, transport ?? new BeaconTransport());
}

class RealAnalyticsEngine implements AnalyticsEngine {
  private readonly base: QuizAnalyticsBase;
  private readonly transport: AnalyticsTransport;
  private queue: QuizAnalyticsEvent[] = [];
  private flushTimer: number | null = null;
  private disposed = false;
  private droppedWarned = false;
  private readonly onVisibility: () => void;
  private readonly onPageHide: () => void;

  constructor(config: RuntimeQuizConfig, transport: AnalyticsTransport) {
    this.transport = transport;
    this.base = {
      publisherId: config.publisherId,
      quizId: config.quizId,
      quizRevision: config.revision,
      schemaVersion: config.schemaVersion,
      quizSessionId: `qs_${uuid()}`,
      device: detectDevice(),
    };
    // A pending partial batch would otherwise be lost when the user leaves.
    // visibilitychange(hidden) covers tab-switch/close (the reliable mobile
    // signal); pagehide covers bfcache + same-tab navigation.
    this.onVisibility = () => {
      if (document.visibilityState === 'hidden') this.flush();
    };
    this.onPageHide = () => this.flush();
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('pagehide', this.onPageHide);
  }

  enqueue(event: AnalyticsEmit): void {
    if (this.disposed) return;
    if (this.queue.length >= MAX_QUEUE) {
      if (!this.droppedWarned) {
        console.warn('[quiz-runtime] analytics: queue cap reached, dropping events');
        this.droppedWarned = true;
      }
      return;
    }
    this.queue.push(this.stamp(event));
    // The terminal event must not sit behind the 400ms window — the page is
    // about to redirect.
    if (event.type === 'quiz_completed') {
      this.flush();
      return;
    }
    this.scheduleFlush();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('pagehide', this.onPageHide);
    this.flush();
  }

  /** Stamp the raw emit with eventId + timestamp, copying only present fields
   *  (exactOptionalPropertyTypes forbids assigning `undefined`). */
  private stamp(event: AnalyticsEmit): QuizAnalyticsEvent {
    const out: QuizAnalyticsEvent = {
      eventId: `evt_${uuid()}`,
      eventName: event.type,
      timestamp: Date.now(),
    };
    if ('stepId' in event) out.stepId = event.stepId;
    if ('stepIndex' in event) out.stepIndex = event.stepIndex;
    if ('stepType' in event) out.stepType = event.stepType;
    if ('optionId' in event) out.optionId = event.optionId;
    if ('optionKind' in event) out.optionKind = event.optionKind;
    if ('optionIndex' in event && event.optionIndex !== undefined) {
      out.optionIndex = event.optionIndex;
    }
    return out;
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null) return;
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, FLUSH_INTERVAL_MS);
  }

  private flush(): void {
    if (this.flushTimer !== null) {
      window.clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.queue.length === 0) return;
    const batch: AnalyticsBatch = { base: this.base, events: this.queue };
    this.queue = [];
    this.send(batch);
  }

  private send(batch: AnalyticsBatch): void {
    for (let attempt = 1; attempt <= MAX_SEND_ATTEMPTS; attempt++) {
      try {
        this.transport.send(batch);
        return;
      } catch (e) {
        if (attempt === MAX_SEND_ATTEMPTS) {
          // Bounded retry exhausted — drop. Analytics must never throw into
          // the render path.
          console.warn('[quiz-runtime] analytics: dropping batch after retries', e);
        }
      }
    }
  }
}
